import { DriverDto, PlantDto, ProductLine, TruckDto } from '@trucktrax/trucktrax-ts-common';
import { createSelector } from 'reselect';
import { ReduxState } from '..';
import { INCOMING_TICKET_PRODUCT_LINES } from '../../constants/appConstants';
import { PlantFilterOption } from '../reducers/mapFilterReducers';
import { MapMarker, PositionDetailsDto, TicketWithCurrentStatusDto } from '../../types';
import { getIdFromUrl } from '../../util/appUtil';
import { createTrackableMapMarkersFromPositions } from '../../util/markerUtil';
import {
  getIncomingMaterialsFolderKey,
  hasIncomingTicketArrived,
  isIncomingTicket,
} from '../../util/statusesUtil';
import { selectedPlantUrlsSelector } from './plantSelectors';

const incomingTicketsListSlice = (state: ReduxState): TicketWithCurrentStatusDto[] =>
  (state.incomingTicketsList as TicketWithCurrentStatusDto[] | undefined) ?? [];

/**
 * DEV-4153: the row currently held for a ticket id. Used by the acknowledge
 * thunk to merge the BE stamp into live state at accept time rather than a
 * render-time snapshot.
 */
export const selectIncomingTicketById = (
  state: ReduxState,
  ticketId: number,
): TicketWithCurrentStatusDto | undefined =>
  incomingTicketsListSlice(state).find(ticket => ticket.id === ticketId);

/**
 * Derives the IM workflow folder key for a ticket from `currentStatus.status`.
 * Mirrors getTicketStatus in IncomingMaterialsPanelContent; intentionally
 * ignores `externalInfo.status` because that field reports events outside the
 * IM workflow (e.g. `DeliveryStart`).
 */
function deriveWorkflowStatus(ticket: TicketWithCurrentStatusDto): string | undefined {
  const status = ticket.currentStatus?.status;
  return status ? getIncomingMaterialsFolderKey(String(status)) : undefined;
}
const plantListSlice = (state: ReduxState): PlantDto[] => state.plantList ?? [];
const incomingPlantListSlice = (state: ReduxState): PlantDto[] => state.incomingPlantList ?? [];
const incomingPlantFiltersSlice = (state: ReduxState): PlantFilterOption[] =>
  state.mapFilters?.incomingPlantFilters ?? [];
const incomingDeselectedProductLinesSlice = (state: ReduxState): ProductLine[] =>
  state.mapFilters?.incomingDeselectedProductLines ?? [];
const incomingPositionListSlice = (state: ReduxState): PositionDetailsDto[] =>
  state.incomingPositionList ?? [];
const regionUrlSlice = (state: ReduxState): string => state.currentRegion?.url ?? '';
const allDriversSlice = (state: ReduxState): DriverDto[] => state.allDrivers ?? [];
const allTrucksSlice = (state: ReduxState): TruckDto[] => state.allTrucks ?? [];
const primaryProductLineSlice = (state: ReduxState): ProductLine => state.primaryProductLine;
// Typed ProductLine[], but the reducer rehydrates it from localStorage as a comma-joined
// string (or null if never stored) until App re-dispatches the token's array on mount —
// and child components mount before App does. Same quirk Ticket.canEditDeliveryLocation
// normalizes.
const secondaryProductLinesSlice = (state: ReduxState) =>
  state.secondaryProductLines as unknown as ProductLine[] | string | null | undefined;

const VALID_PRODUCT_LINES = new Set<string>(
  Object.values(ProductLine).filter(pl => pl !== ProductLine.None),
);

/**
 * DEV-3820 (PO decision): Incoming Materials plants are gated by the user's product
 * lines — primary plus secondary, the same set the product-line switcher offers — sent as
 * getPlantList's `inProductLines`; its region arg still scopes by region. Shared by
 * IncomingMaterialsView and IncomingMaterialsSubscription so the filter list and the
 * notification seed cannot drift.
 *
 * '' when the user has no product lines (or they haven't loaded): callers must then skip the fetch,
 * since getPlantList without `inProductLines` returns every product line's plants.
 */
export const incomingPlantProductLinesSelector = createSelector(
  [primaryProductLineSlice, secondaryProductLinesSlice],
  (primary, secondary): string => {
    const secondaryList = typeof secondary === 'string' ? secondary.split(',') : (secondary ?? []);
    const userProductLines = [primary, ...secondaryList].filter(pl => VALID_PRODUCT_LINES.has(pl));
    return Array.from(new Set(userProductLines)).join(',');
  },
);

export const activePlantListSelector = createSelector(
  [plantListSlice],
  (plantList: PlantDto[]): PlantDto[] => plantList.filter(p => !p.archived && !p.deleted),
);

/** Active plants filtered to only those selected in map filters (empty selection = all). */
export const filteredActivePlantListSelector = createSelector(
  [activePlantListSelector, selectedPlantUrlsSelector],
  (allPlants: PlantDto[], selectedUrls: string[]): PlantDto[] => {
    if (selectedUrls.length === 0) return allPlants;
    const selectedSet = new Set(selectedUrls);
    return allPlants.filter(p => selectedSet.has(p.url ?? ''));
  },
);

export const incomingActivePlantListSelector = createSelector(
  [incomingPlantListSlice],
  (plantList: PlantDto[]): PlantDto[] => plantList.filter(p => !p.archived && !p.deleted),
);

/** The Destination Plants checkbox list: every active incoming plant, defaulting to selected. */
export const incomingMapFilterPlantListSelector = createSelector(
  [incomingActivePlantListSelector, incomingPlantFiltersSlice],
  (
    incomingPlants: PlantDto[],
    existingMapFilterPlants: PlantFilterOption[],
  ): PlantFilterOption[] => {
    const selectedByUrl = new Map<string, boolean>();
    existingMapFilterPlants.forEach(plant => {
      if (plant.url) {
        selectedByUrl.set(plant.url, plant.isSelected);
      }
    });

    return incomingPlants.map(plant => ({
      id: plant.id,
      name: plant.name,
      url: plant.url,
      isSelected: selectedByUrl.get(plant.url ?? '') ?? true,
    }));
  },
);

/**
 * DEV-3820: derived from the displayed Destination Plants list, not from the main map's
 * `selectedPlantUrlsSelector`. That slice is scoped to the selected product line, so
 * intersecting with it silently dropped every cement plant the widened fetch now returns.
 */
export const selectedIncomingPlantUrlsSelector = createSelector(
  [incomingMapFilterPlantListSelector],
  (mapFilterPlants: PlantFilterOption[]): string[] =>
    mapFilterPlants
      .filter(plant => plant.isSelected)
      .map(plant => plant.url ?? '')
      .filter(Boolean),
);

/**
 * DEV-3820: the incoming Destination Plants list is built from the loaded plants and
 * defaults to all-selected, so an empty selection with plants loaded is a deliberate
 * deselect-all and shows nothing. With no plants loaded (fetch pending or failed) the output
 * is empty either way. The main map's `filteredActivePlantListSelector` keeps its
 * empty-means-all fallback, where an empty selection genuinely is ambiguous.
 */
export const filteredIncomingActivePlantListSelector = createSelector(
  [incomingActivePlantListSelector, selectedIncomingPlantUrlsSelector],
  (allIncomingPlants: PlantDto[], selectedUrls: string[]): PlantDto[] => {
    const selectedSet = new Set(selectedUrls);
    return allIncomingPlants.filter(p => selectedSet.has(p.url ?? ''));
  },
);

/**
 * Returns tickets that are "incoming" — those with a destinationPlant set.
 * A ticket gets a destinationPlant when its delivery destination falls within
 * a plant's geofence, meaning materials are being delivered TO a plant.
 *
 * DEV-4997: drops tickets whose truck has left the destination plant
 * (Returning/InYard). Those tickets can stay open, so the BE close event
 * that normally removes them never arrives.
 */
export const incomingTicketsSelector = createSelector(
  [incomingTicketsListSlice],
  (ticketsList: TicketWithCurrentStatusDto[]): TicketWithCurrentStatusDto[] =>
    ticketsList.filter(isIncomingTicket),
);

/**
 * Incoming tickets bound for a destination plant selected in the Destination Plants filter.
 * Feeds the IM map markers, the sidebar panel and the tickets popover.
 *
 * DEV-3820: the Destination Plants list is built from the loaded plants and defaults to
 * all-selected, so an empty selection with plants loaded is a deliberate deselect-all and
 * filters every ticket out. An empty plant list means not loaded yet (mount, region change)
 * or the plant fetch failed — nothing can have been deselected, so tickets pass through
 * unfiltered; IncomingMaterialsSubscription already scopes them to the region's plants.
 */
export const incomingTicketsForSelectedPlantsSelector = createSelector(
  [incomingTicketsSelector, incomingActivePlantListSelector, selectedIncomingPlantUrlsSelector],
  (
    tickets: TicketWithCurrentStatusDto[],
    plants: PlantDto[],
    selectedUrls: string[],
  ): TicketWithCurrentStatusDto[] => {
    if (plants.length === 0) return tickets;
    const selectedSet = new Set(selectedUrls);
    return tickets.filter(t => selectedSet.has(t.destinationPlant?.url ?? ''));
  },
);

/**
 * DEV-5225: the product lines the IM ticket panel's Product Line filter keeps, or undefined
 * when it applies no filter — every option checked, or none (unchecking all means no filter).
 */
export const incomingTicketProductLineFilterSelector = createSelector(
  [incomingDeselectedProductLinesSlice],
  (deselected: ProductLine[]): ProductLine[] | undefined => {
    const checked = INCOMING_TICKET_PRODUCT_LINES.filter(pl => !deselected.includes(pl));
    return checked.length === 0 || checked.length === INCOMING_TICKET_PRODUCT_LINES.length
      ? undefined
      : checked;
  },
);

/**
 * DEV-5225: the tickets popover's current tickets — the destination plant selection narrowed
 * by the ticket panel's Product Line filter. Only the panel uses it, so the map markers and
 * sidebar counts are not product-line filtered.
 */
export const incomingTicketsForTicketPanelSelector = createSelector(
  [incomingTicketsForSelectedPlantsSelector, incomingTicketProductLineFilterSelector],
  (
    tickets: TicketWithCurrentStatusDto[],
    productLines: ProductLine[] | undefined,
  ): TicketWithCurrentStatusDto[] => {
    if (!productLines) return tickets;
    return tickets.filter(t => {
      const productLine = t.externalInfo?.plantProductLine;
      return !!productLine && productLines.includes(productLine);
    });
  },
);

/**
 * True when any open incoming ticket has arrived (past ToJob). Drives the red
 * notification dot on the Incoming Materials nav route button (DEV-4154).
 */
export const hasArrivedIncomingTicketsSelector = createSelector(
  [incomingTicketsSelector],
  (incomingTickets: TicketWithCurrentStatusDto[]): boolean =>
    incomingTickets.some(hasIncomingTicketArrived),
);

/**
 * Returns map markers for trucks that have incoming tickets bound for a selected
 * destination plant.
 * Builds markers from positionList directly (bypassing the workflow filter
 * in activeMarkersSelector), and enriches positions with the ticket's status
 * when the position lacks one — so markers get correct colors and arrows.
 */
const incomingActiveMarkersSelector = createSelector(
  [
    incomingPositionListSlice,
    regionUrlSlice,
    allDriversSlice,
    allTrucksSlice,
    incomingTicketsForSelectedPlantsSelector,
  ],
  (
    positions: PositionDetailsDto[],
    regionUrl: string,
    allDrivers: DriverDto[],
    allTrucks: TruckDto[],
    incomingTickets: TicketWithCurrentStatusDto[],
  ): MapMarker[] => {
    if (!incomingTickets.length || !positions.length) return [];

    // Build a map from truck ID → ticket for quick lookup
    const ticketByTruckId = new Map<string, TicketWithCurrentStatusDto>();
    incomingTickets.forEach(ticket => {
      const truckId = getIdFromUrl(ticket.truck?.url);
      if (truckId) ticketByTruckId.set(truckId, ticket);
    });

    // Match positions to incoming tickets by ticket.truck.url (authoritative).
    // The position's trackedObject is unreliable for incoming materials —
    // external/GT-Lite drivers have no trackedObject, and the standard
    // UPDATE_POSITION merge can synthesize a driver-shaped one. Looking up
    // by ticket.truck.url avoids both failure modes.
    //
    // We also force the position's trackedObject to point at the ticket's
    // truck before building markers, so each marker is keyed by truck id.
    // This matches what IncomingMaterialsPanelContent's truck-row click
    // handler expects (it looks up incomingMarkers by truck id).
    const incomingPositions: PositionDetailsDto[] = [];
    positions.forEach(pos => {
      const positionTruckUrl = pos.ticket?.truck?.url ?? pos.trackedObject?.url;
      const truckId = getIdFromUrl(positionTruckUrl);
      if (!truckId) return;
      const ticket = ticketByTruckId.get(truckId);
      if (!ticket) return;
      // Prefer the ticket's own truck.truckAlias (set by the BE on the IM
      // ticket payload, same source the sidebar uses in
      // IncomingMaterialsPanelContent.getTruckDisplayName) and fall back to
      // externalInfo.truckAlias only when the embedded truck object is
      // unaliased. allTrucks is region+productLine scoped, so it doesn't
      // cover cross-region external trucks delivering into the user's plants
      // — which is the whole reason the IM map exists.
      const truckTrackedObject = {
        url: ticket.truck?.url,
        truckAlias: ticket.truck?.truckAlias ?? ticket.externalInfo?.truckAlias,
      };
      // Drive marker color/arrow from the ticket's real workflow status
      // (from currentStatus). externalInfo.status is unreliable —
      // see ticketsService.fetchIncomingTicketsWithCurrentStatus.
      const workflowStatus = deriveWorkflowStatus(ticket);
      // Merge the full ticket (which has geoZone/address) onto the position so
      // the InfoPane's hasDestination check and updatePositionWithTruckEta's
      // destination lookup both succeed. The /incomingmaterials/position-details
      // payload's embedded ticket is a thin shape that lacks these fields.
      const mergedTicket = { ...(pos.ticket ?? {}), ...ticket };
      // DEV-3924: product-line-keyed marker UI (status tooltip names, status bucketing
      // in PositionMarker) must come from the paired ticket's origination plant, never
      // the truck's primary product line carried on the position. Unresolved ⇒ None,
      // so the marker shows "Unknown" rather than another product line's names.
      const ticketProductLine = ticket.externalInfo?.plantProductLine;
      const markerProductLine =
        ticketProductLine && ticketProductLine !== ProductLine.None
          ? ticketProductLine
          : ProductLine.None;
      const enriched: PositionDetailsDto = {
        ...pos,
        ticket: mergedTicket as PositionDetailsDto['ticket'],
        trackedObject: truckTrackedObject as PositionDetailsDto['trackedObject'],
        productLine: markerProductLine,
      };
      if (workflowStatus) {
        enriched.status = workflowStatus;
        enriched.statusTimestamp = ticket.currentStatus?.timestamp ?? pos.statusTimestamp;
      }
      incomingPositions.push(enriched);
    });

    // Incoming materials shows external trucks delivering from other regions,
    // so we bypass the region filter and include GT-Lite drivers (no trackedObject).
    return createTrackableMapMarkersFromPositions(
      incomingPositions,
      regionUrl,
      allDrivers,
      allTrucks,
      true,
      true,
    );
  },
);

export default incomingActiveMarkersSelector;
